/**
 * Athlete Hub Phase 1 — pure rules shared by the athlete dashboard and admin overview.
 * Profile completeness = public content only (core + modules she chose).
 * Launch readiness = publication gates (Quick Start, SIIS review, approval of the exact version, consents, admin publish).
 * Waiting on SIIS never counts as "incomplete".
 */
import {
  hasDirectSupport, richText, socialLinkCount, text, validPhotos, validVideos, type ProfileAthlete,
} from "@/lib/profileModules";

export const STAGES = ["invited", "started", "siis_review", "athlete_approval", "ready", "published"] as const;
export type Stage = (typeof STAGES)[number];
export const STAGE_LABEL: Record<Stage, string> = {
  invited: "Invited", started: "Started", siis_review: "SIIS Review",
  athlete_approval: "Athlete Approval", ready: "Ready", published: "Published",
};
export const CONSENT_VERSION = "2026-10-v1";

export type ModuleChoices = {
  direct_support?: boolean; work_with?: boolean; highlights?: boolean; causes?: boolean;
  her_collection?: "interested" | "not_now";
};
export type HubState = {
  stage: Stage;
  quick_start_submitted_at: string | null;
  module_choices: ModuleChoices;
  approved_current_version: boolean;
  approved_at: string | null;
  consents: { profile_publication: boolean; photo_likeness: boolean };
  published?: boolean;
};

export type CheckItem = { key: string; label: string; done: boolean; where: "profile" | "causes" | "support" | "quick_start" };

const anyPhoto = (a: ProfileAthlete) => Boolean(text((a as any).image_path));

/** Content checklist. Optional modules count only when she chose them. */
export const completenessItems = (a: ProfileAthlete, choices: ModuleChoices, causeCount: number): CheckItem[] => {
  const items: CheckItem[] = [
    { key: "basics", label: "Basic information", done: Boolean(text(a.name)) && Boolean(text(a.sport)) && a.sport !== "TBD", where: "profile" },
    { key: "photo", label: "Profile photo", done: anyPhoto(a), where: "profile" },
    { key: "positioning", label: "One-line positioning", done: Boolean(text(a.tagline)), where: "profile" },
    { key: "story", label: "Your story", done: Boolean(richText(a.bio) || richText((a as any).story)), where: "profile" },
    { key: "social", label: "Social accounts", done: socialLinkCount(a) > 0, where: "profile" },
  ];
  if (choices.highlights) items.push({ key: "highlights", label: "Highlights (photos or videos)", done: validPhotos(a, (p) => p).length > 0 || validVideos(a).length > 0, where: "profile" });
  if (choices.causes) items.push({ key: "causes", label: "Causes you care about", done: causeCount > 0 || Boolean(text((a as any).causes_text)), where: "causes" });
  if (choices.work_with) items.push({ key: "work_with", label: "Partnership interests", done: Boolean(text((a as any).partnership_interests)) || Boolean(text(a.booking_inquiry_email)), where: "profile" });
  if (choices.direct_support) items.push({ key: "direct_support", label: "Direct Support set up", done: hasDirectSupport(a).active, where: "support" });
  return items;
};

export const percent = (items: { done: boolean }[]) =>
  items.length ? Math.round((items.filter((i) => i.done).length / items.length) * 100) : 100;

export type Gate = { key: string; label: string; done: boolean; owner: "athlete" | "siis" };

/** Publication gates, separate from content. */
export const readinessGates = (h: HubState): Gate[] => {
  const idx = STAGES.indexOf(h.stage);
  return [
    { key: "quick_start", label: "Quick Start submitted", done: Boolean(h.quick_start_submitted_at), owner: "athlete" },
    { key: "siis_review", label: "SIIS review", done: idx >= STAGES.indexOf("athlete_approval"), owner: "siis" },
    { key: "approval", label: "You approved this version of your profile", done: h.approved_current_version, owner: "athlete" },
    { key: "consent_profile", label: "Profile publication consent", done: h.consents.profile_publication, owner: "athlete" },
    { key: "consent_likeness", label: "Photo and likeness consent", done: h.consents.photo_likeness, owner: "athlete" },
    { key: "publish", label: "Published by SIIS", done: h.stage === "published" || Boolean(h.published), owner: "siis" },
  ];
};

export type Task = { key: string; label: string; kind: "athlete" | "waiting"; where?: CheckItem["where"] | "approve" | "consent" };

/** Combined "What needs attention" list. Waiting-on-SIIS items are informational, never athlete tasks. */
export const attentionTasks = (h: HubState, content: CheckItem[]): Task[] => {
  const t: Task[] = [];
  if (!h.quick_start_submitted_at) t.push({ key: "quick_start", label: "Answer your Quick Start questions", kind: "athlete", where: "quick_start" });
  if (h.stage === "athlete_approval" && !h.approved_current_version) t.push({ key: "approve", label: "Review and approve your profile", kind: "athlete", where: "approve" });
  if (!h.consents.profile_publication || !h.consents.photo_likeness) t.push({ key: "consent", label: "Confirm your publication and photo consent", kind: "athlete", where: "consent" });
  if (!h.module_choices.her_collection) t.push({ key: "her_collection", label: "Tell SIIS whether you're interested in Her Collection", kind: "athlete", where: "quick_start" });
  for (const c of content) if (!c.done) t.push({ key: `content_${c.key}`, label: `Add: ${c.label}`, kind: "athlete", where: c.where });
  if (h.stage === "siis_review") t.push({ key: "wait_review", label: "SIIS is preparing your profile", kind: "waiting" });
  if (h.stage === "ready") t.push({ key: "wait_publish", label: "Ready — SIIS will publish your profile", kind: "waiting" });
  if (h.stage === "published" && !h.approved_current_version && h.approved_at)
    t.push({ key: "changed", label: "Your live profile changed since you approved it", kind: "waiting" });
  return t;
};

/** Admin "next task" — first blocking item. */
export const nextAdminTask = (h: HubState, contentPct: number, claimed: boolean): string => {
  if (!claimed && h.stage === "invited") return "Send claim link";
  if (!h.quick_start_submitted_at && STAGES.indexOf(h.stage) < 2) return "Waiting on athlete Quick Start";
  if (h.stage === "siis_review") return "SIIS: prepare profile, then send for approval";
  if (h.stage === "athlete_approval") return h.approved_current_version ? "Waiting on athlete consent" : "Waiting on athlete approval";
  if (h.stage === "ready") return "Publish when ready";
  if (h.stage === "published" && h.approved_at && !h.approved_current_version) return "Live profile differs from approved version";
  if (contentPct < 100) return "Profile content incomplete";
  return "—";
};
